import cx from 'clsx';
import * as React from 'react';

import { useAutoplay } from '@/components/Carousel/Autoplay';
import Indicator from '@/components/Carousel/Indicator';
import { ArrowLeft, ArrowRight } from '@/components/icons';
import { isEqual } from '@/utils/lodash-alternatives';

import styles from './Infinite.module.scss';
import { prepareInfiniteChildrenArray } from './utils';

interface CarouselInfiniteProps {
  children: React.ReactNode[];
  specifiedSlidesAmount?: number;
  autoplay?: number;
}

const CarouselInfinite: React.FC<CarouselInfiniteProps> = React.memo(
  props => {
    const { children, specifiedSlidesAmount, autoplay = 0 } = props;
    const [infiniteChildren, setInfiniteChildren] = React.useState(
      prepareInfiniteChildrenArray(children)
    );
    const [infiniteWrapperStyle, setInfiniteWrapperStyle] = React.useState({});
    const [activeSlideIdx, setActiveSlideIdx] = React.useState<number>(1);
    const [direction, setDirection] = React.useState<-1 | 0 | 1>(0);
    const [shouldContinueScroll, setShouldContinueScroll] = React.useState(0);
    let dragStartPosition = 0;

    React.useEffect(() => {
      setInfiniteChildren(prepareInfiniteChildrenArray(children));
    }, [children]);

    const renderedSlidesAmount = specifiedSlidesAmount
      ? specifiedSlidesAmount
      : children.length;

    const updateElementStyle = (val: string, immediate?: boolean) => {
      let style = {};
      if (immediate) {
        style = {
          transition: 'none',
        };
      } else {
        style = {
          transition: 'transform 500ms ease 0s',
        };
      }
      style = {
        ...style,
        transform: `translateX(${val})`,
      };
      setInfiniteWrapperStyle(style);
    };

    React.useEffect(() => {
      if (shouldContinueScroll !== 0) {
        setTimeout(
          () => updateElementStyle(`${shouldContinueScroll}%`, false),
          0
        );
        setShouldContinueScroll(0);
      }
    }, [shouldContinueScroll]);

    const moveToSlide = React.useCallback(
      (direction: 1 | -1) => {
        let nextSlideIdx;
        if (renderedSlidesAmount === 1) return;
        if (direction === 1) {
          nextSlideIdx =
            activeSlideIdx + 1 > renderedSlidesAmount ? 1 : activeSlideIdx + 1;
        } else {
          nextSlideIdx =
            activeSlideIdx - 1 < 0
              ? renderedSlidesAmount - 1
              : activeSlideIdx - 1;
        }

        if (direction === 1 && nextSlideIdx < activeSlideIdx) {
          updateElementStyle(`0%`, true);
          setShouldContinueScroll(-nextSlideIdx * 100);
        } else if (direction === -1 && nextSlideIdx > activeSlideIdx) {
          updateElementStyle(`-${renderedSlidesAmount * 100}%`, true);
          setShouldContinueScroll(-nextSlideIdx * 100);
        } else {
          updateElementStyle(`-${nextSlideIdx * 100}%`);
        }

        setActiveSlideIdx(nextSlideIdx);
      },
      [activeSlideIdx, renderedSlidesAmount]
    );

    React.useEffect(() => {
      if (direction !== 0) {
        moveToSlide(direction);
        setDirection(0);
      }
    }, [direction, moveToSlide]);

    const handleMoveToSlide = (moveToSlide: () => void) => {
      if (autoplay) {
        stopAutoplay();
        moveToSlide();
        startAutoplay();
      } else {
        moveToSlide();
      }
    };

    const handleNextSlideClick = () => {
      handleMoveToSlide(moveToNextSlide);
    };

    const handlePrevSlideClick = () => {
      handleMoveToSlide(moveToPreviousSlide);
    };

    const moveToNextSlide = () => {
      setDirection(1);
    };

    const moveToPreviousSlide = () => {
      setDirection(-1);
    };

    const [startAutoplay, stopAutoplay] = useAutoplay(
      autoplay,
      moveToNextSlide
    );

    const handleSlideChange = (e: any) => {
      const diff = dragStartPosition - e.pageX;
      if (diff === 0) return;
      else if (diff > 0) {
        handleNextSlideClick();
      } else {
        handlePrevSlideClick();
      }
    };

    const resetTransition = () => {
      if (activeSlideIdx >= renderedSlidesAmount) {
        updateElementStyle(`0%`, true);
        setActiveSlideIdx(0);
      } else if (activeSlideIdx < 1) {
        updateElementStyle(`-${renderedSlidesAmount * 100}%`, true);
        setActiveSlideIdx(renderedSlidesAmount);
      }
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          setActiveSlideIdx(activeSlideIdx - 1);
        } else if (activeSlideIdx < renderedSlidesAmount) {
          setActiveSlideIdx(activeSlideIdx + 1);
        }
      }
    };

    const onArrowKeyDown = (e: React.KeyboardEvent) => {
      e.preventDefault();
    };

    return (
      <div className={styles.containerOuter}>
        <div className={cx(styles.container)} data-testid="carousel-container">
          <ul
            data-testid="infiniteList"
            onDragEnd={handleSlideChange}
            onTransitionEnd={() => resetTransition()}
            onTransitionEndCapture={() => resetTransition()}
            onDragStart={e => (dragStartPosition = e.pageX)}
            draggable
            style={infiniteWrapperStyle}
            className={cx(
              styles.list,
              renderedSlidesAmount > 1 && styles.wrapper
            )}
            onKeyDown={onKeyDown}
          >
            {infiniteChildren}
          </ul>
        </div>
        {renderedSlidesAmount > 1 ? (
          <div onKeyDown={onArrowKeyDown}>
            <span
              onClick={handleNextSlideClick}
              className={cx(styles.next, styles.arrowWrapper)}
            >
              <ArrowRight className={styles.arrow} />
            </span>
            <span
              onClick={handlePrevSlideClick}
              className={cx(styles.prev, styles.arrowWrapper)}
            >
              <ArrowLeft className={styles.arrow} />
            </span>
          </div>
        ) : null}
        <Indicator
          amount={renderedSlidesAmount}
          active={
            activeSlideIdx - 1 < 0
              ? renderedSlidesAmount - 1
              : activeSlideIdx - 1
          }
        />
      </div>
    );
  },
  (props, newProps) => isEqual(props, newProps)
);

export default CarouselInfinite;
